import { BaseCard } from '@/shared/components'
import { BaseCopyButton } from '@/shared/components/BaseCopyButton/BaseCopyButton'
import { DealIcon } from '@/shared/components/icons/DealIcon'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { formatRequisitToType } from '@/features/requisites'
import { getBankMeta } from '@/shared/utils/getBankMeta'
import { getShortenId } from '@/shared/utils/getShortenId'
import { TopupStatusIcons, TopupStatusTitles } from '@/entities/topups'
import { PaymentRequisiteToType } from '@/entities/paymentRequisit'
import type { ReferralTopupModel } from './columns'
import type { IconStatus } from '@/shared/utils'

type Props = {
  topup: ReferralTopupModel
}

export const MobileBlockReferralTopup = ({ topup }: Props) => {
  const subtitleClasses = 'text-text-secondary'
  const dataClasses = 'font-medium'
  const dataRowClasses = 'flex justify-between'

  // Проверяем наличие factAmountFrom/factAmountTo (есть в TopupsModel)
  const hasFactAmount = 'factAmountFrom' in topup || 'factAmountTo' in topup
  const amountFrom = (hasFactAmount && 'factAmountFrom' in topup && topup.factAmountFrom && topup.factAmountFrom !== 0)
    ? topup.factAmountFrom 
    : topup.amountFrom
  const amountTo = (hasFactAmount && 'factAmountTo' in topup && topup.factAmountTo && topup.factAmountTo !== 0)
    ? topup.factAmountTo 
    : topup.amountTo

  // Определяем тип реквизита для форматирования
  let methodType: PaymentRequisiteToType | undefined
  let requisiteValue = ''
  
  if ('paymentRequisitToType' in topup && 'paymentRequisitToValue' in topup) {
    methodType = topup.paymentRequisitToType as PaymentRequisiteToType
    requisiteValue = topup.paymentRequisitToValue || ''
  } else if ('paymentRequisitProfile' in topup && topup.paymentRequisitProfile) {
    methodType = topup.paymentRequisitProfile.type
    requisiteValue = topup.paymentRequisitProfile.value || ''
  }

  const requisite = formatRequisitToType(
    methodType || PaymentRequisiteToType.CardNumber,
    requisiteValue || ''
  )

  const bankName = topup.requisitProfile?.bankToName || ''
  const { title: bankTitle } = bankName ? getBankMeta(bankName) : { title: '' }

  // Используем methodFrom из API ответа
  const method = ('methodFrom' in topup && topup.methodFrom)
    ? (topup.methodFrom as string)
    : '—'

  const columnsData = [
    {
      title: 'ID сделки:',
      data: topup.id,
      copyBtn: true,
    },
    {
      title: 'Курс:',
      data: topup.exchangeRate ? `${topup.exchangeRate} RUB` : '-',
    },
    {
      title: 'Метод:',
      data: method,
    },
    {
      title: 'Банк:',
      data: bankTitle,
    },
    {
      title: 'Реквизит:',
      data: requisite,
    },
  ]

  const status = topup.status
  const title = status !== undefined && status !== null 
    ? (TopupStatusTitles[status as keyof typeof TopupStatusTitles] || '')
    : ''
  const iconStatus: IconStatus = status !== undefined && status !== null
    ? (TopupStatusIcons[status as keyof typeof TopupStatusIcons] || 'none')
    : 'none'

  return (
    <BaseCard variant="secondary" className="flex flex-col mb-4 last:mb-0 px-5 text-sm">
      <div className="flex justify-between items-center gap-1 mb-6">
        <ItemLayout
          icon={<DealIcon status={iconStatus} />}
          title={<span className="mr-2 font-medium">{title}</span>}
          subtitle={formatDate(topup.createdAt || '')}
        />
      </div>

      <div>
        <div className={`${dataRowClasses} mb-4`}>
          <span className="font-klein font-medium text-[18px]">
            {formatCurrency(amountTo ?? 0, topup.currencyTo ?? 643)}
          </span>
          <span className="text-text-secondary">
            {formatCurrency(amountFrom ?? 0, topup.currencyFrom ?? 643)}
          </span>
        </div>

        <div className="flex flex-col gap-[10px]">
          {columnsData.map((el, i) => (
            <div key={i} className={dataRowClasses}>
              <span className={subtitleClasses}>{el.title}</span>
              <div className="flex items-center gap-1">
                <span className={el.copyBtn ? `${dataClasses} text-primary` : `${dataClasses}`}>
                  {el.copyBtn ? getShortenId(el.data as string || '', 'long') : el.data}
                </span>
                {el.copyBtn && <BaseCopyButton text={el.data as string || ''} color="primary" size="mini" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </BaseCard>
  )
}

